/**
 * Permissions Service — Product Portal
 *
 * Fetches and resolves role-module permissions for the current user.
 * Called after login to determine which modules are visible to the user.
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import { useAuthStore } from '@/stores/auth.store';

export type ApiModuleKey =
  | 'PRODUCT_MANAGEMENT'
  | 'TENANT_MANAGEMENT'
  | 'USER_MANAGEMENT'
  | 'ENTERPRISES_MANAGEMENT'
  | 'SETTINGS'
  | 'RECALL'
  | 'AUDIT_LOG';

export type ApiRoleKey =
  | 'ADMIN'
  | 'PRODUCTION_SUPPORT'
  | 'PRODUCT_TEAM';

type RoleModuleMatrixRow = Record<ApiModuleKey, boolean>;
type RoleModuleMatrix = Record<ApiRoleKey, RoleModuleMatrixRow>;

interface RoleModuleMatrixApiResponse {
  hospital_id: string;
  matrix: RoleModuleMatrix;
  source: 'hospital' | 'default';
  updated_by?: string;
  updated_at?: string;
}

/**
 * UI-friendly module permissions representation (CONTROLPLANE modules only)
 */
export interface ModulePermissions {
  productManagement: boolean;
  tenantManagement: boolean;
  userManagement: boolean;
  enterprisesManagement: boolean;
  settings: boolean;
  recall: boolean;
  auditLogs: boolean;
}

/**
 * Convert API role module matrix row to UI permissions
 */
function toUIPermissions(row: RoleModuleMatrixRow): ModulePermissions {
  return {
    productManagement:    row.PRODUCT_MANAGEMENT,
    tenantManagement:     row.TENANT_MANAGEMENT,
    userManagement:       row.USER_MANAGEMENT,
    enterprisesManagement: row.ENTERPRISES_MANAGEMENT,
    settings:             row.SETTINGS,
    recall:               row.RECALL,
    auditLogs:            row.AUDIT_LOG,
  };
}

/**
 * Merge multiple role permissions using union logic
 * (a module is accessible if ANY role grants access)
 */
function mergePermissions(rolePermissions: ModulePermissions[]): ModulePermissions {
  return {
    productManagement:    rolePermissions.some(p => p.productManagement),
    tenantManagement:     rolePermissions.some(p => p.tenantManagement),
    userManagement:       rolePermissions.some(p => p.userManagement),
    enterprisesManagement: rolePermissions.some(p => p.enterprisesManagement),
    settings:             rolePermissions.some(p => p.settings),
    recall:               rolePermissions.some(p => p.recall),
    auditLogs:            rolePermissions.some(p => p.auditLogs),
  };
}

/**
 * Map Cognito role strings to API role keys
 */
function mapCognitoRoleToApiRole(cognitoRole: string): ApiRoleKey | null {
  const roleMapping: Record<string, ApiRoleKey> = {
    'admin':              'ADMIN',
    'production_support': 'PRODUCTION_SUPPORT',
    'product_team':       'PRODUCT_TEAM',
  };

  return roleMapping[cognitoRole.toLowerCase()] || null;
}

/**
 * Fetch the role-module permission matrix for the current tenant.
 * GET /onboarding/api/v1/tenants/{hospitalId}/role-module-matrix/?apptype=CONTROLPLANE
 */
export async function fetchRoleModuleMatrix(): Promise<RoleModuleMatrix> {
  const authStore = useAuthStore();
  const { hospitalId } = authStore;

  if (!hospitalId) {
    throw new Error('hospitalId is not available — user must be authenticated');
  }

  const api = getApiClient();
  const url = `${API_ROUTES.ONBOARDING}/tenants/${encodeURIComponent(hospitalId)}/role-module-matrix/`;

  const response = await api.get<RoleModuleMatrixApiResponse>(
    url,
    { params: { apptype: 'CONTROLPLANE' } },
  );

  return response.data.matrix;
}

/**
 * Resolve module permissions for the current user based on their roles.
 *
 * Logic:
 * 1. Fetch the role-module-matrix from the backend
 * 2. Map each of user's Cognito roles to API role keys
 * 3. Look up each role's permissions in the matrix
 * 4. Merge using union logic (module accessible if ANY role grants access)
 */
export async function resolvePermissionsForUser(): Promise<ModulePermissions> {
  const authStore = useAuthStore();
  const { parsedRoles, hospitalId } = authStore;

  if (!parsedRoles || parsedRoles.length === 0) {
    throw new Error('User has no roles assigned');
  }

  // Map Cognito roles to API role keys (needed for both paths below)
  const apiRoles = parsedRoles
    .map(role => mapCognitoRoleToApiRole(role))
    .filter((role): role is ApiRoleKey => role !== null);

  // When hospitalId is unavailable (e.g. login response omitted hospital_id),
  // the matrix API cannot be called. Grant full access for ADMIN, PRODUCTION_SUPPORT,
  // and PRODUCT_TEAM roles — the backend still enforces real authorization on every API call.
  if (!hospitalId) {
    const hasAdminAccess = apiRoles.includes('ADMIN') || apiRoles.includes('PRODUCTION_SUPPORT') || apiRoles.includes('PRODUCT_TEAM');
    return {
      productManagement:    hasAdminAccess,
      tenantManagement:     hasAdminAccess,
      userManagement:       hasAdminAccess,
      enterprisesManagement: hasAdminAccess,
      settings:             hasAdminAccess,
      recall:               hasAdminAccess,
      auditLogs:            hasAdminAccess,
    };
  }

  // Fetch the full matrix
  const matrix = await fetchRoleModuleMatrix();

  if (apiRoles.length === 0) {
    // Deny all access (fail secure)
    return {
      productManagement:    false,
      tenantManagement:     false,
      userManagement:       false,
      enterprisesManagement: false,
      settings:             false,
      recall:               false,
      auditLogs:            false,
    };
  }

  // Get permissions for each role and merge them
  const rolePermissions = apiRoles.map(roleKey => {
    const roleRow = matrix[roleKey];
    if (!roleRow) {
      return {
        productManagement:    false,
        tenantManagement:     false,
        userManagement:       false,
        enterprisesManagement: false,
        settings:             false,
        recall:               false,
        auditLogs:            false,
      };
    }
    return toUIPermissions(roleRow);
  });

  // Merge permissions using union logic
  const merged = mergePermissions(rolePermissions);

  return merged;
}

/**
 * Check if a specific module is accessible
 */
export function isModuleAccessible(
  permissions: Partial<ModulePermissions>,
  moduleKey: keyof ModulePermissions,
): boolean {
  return permissions[moduleKey] !== false;
}
